import type { AppSelectOption } from '../../../components/AppSelectDropdown';
import type { CompanyResponsiblePerson, CompanySettings } from '../../../types';
import { GERMAN_STATES, normalizeGermanStateCode } from '../../../utils/germanStates';

export type CompanySettingsCardId = 'unternehmen' | 'kontakt' | 'rechtlich' | 'bank';

export type CompanyFormState = {
  company_name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country_code: string;
  state_code: string;
  phone: string;
  email: string;
  website: string;
  director_role: string;
  responsible_persons: CompanyResponsiblePerson[];
  vat_id: string;
  tax_number: string;
  commercial_register_number: string;
  registry_court: string;
  bank_name: string;
  bank_account_holder: string;
  iban: string;
  bic: string;
};

/** Optionen „Verantwortliche Person (Typ)“ (Rechtliches) */
export const DIRECTOR_ROLE_OPTIONS: readonly string[] = [
  'Geschäftsführer',
  'Geschäftsführerin',
  'Geschäftsführend',
  'Inhaber',
  'Inhaberin',
  'Inhaber:innen',
  'Gesellschafter',
  'Gesellschafterin',
  'Gesellschafter:innen',
  'Komplementäre',
  'Träger',
  'Vorstand',
];

export const GERMAN_STATE_OPTIONS: AppSelectOption[] = [
  { value: '', label: 'Bitte wählen …' },
  ...GERMAN_STATES.map((state) => ({ value: state.value, label: state.label })),
];

export const GERMAN_STATE_LABEL_BY_CODE = new Map<string, string>(
  GERMAN_STATES.map((x) => [x.value, x.label])
);

export const emptyCompanyForm: CompanyFormState = {
  company_name: '',
  street: '',
  house_number: '',
  postal_code: '',
  city: '',
  country_code: 'DE',
  state_code: '',
  phone: '',
  email: '',
  website: '',
  director_role: '',
  responsible_persons: [],
  vat_id: '',
  tax_number: '',
  commercial_register_number: '',
  registry_court: '',
  bank_name: '',
  bank_account_holder: '',
  iban: '',
  bic: '',
};

export function companySettingsFromApi(d: CompanySettings): CompanyFormState {
  const persons: CompanyResponsiblePerson[] = Array.isArray(d.responsible_persons)
    ? d.responsible_persons.map((p) => ({
        name: typeof p?.name === 'string' ? p.name : '',
      }))
    : [];
  return {
    company_name: d.company_name ?? '',
    street: d.street ?? '',
    house_number: d.house_number ?? '',
    postal_code: d.postal_code ?? '',
    city: d.city ?? '',
    country_code: 'DE',
    state_code: normalizeGermanStateCode(d.state_code) ?? '',
    phone: d.phone ?? '',
    email: d.email ?? '',
    website: d.website ?? '',
    director_role: d.director_role ?? '',
    responsible_persons: persons,
    vat_id: d.vat_id ?? '',
    tax_number: d.tax_number ?? '',
    commercial_register_number: d.commercial_register_number ?? '',
    registry_court: d.registry_court ?? '',
    bank_name: d.bank_name ?? '',
    bank_account_holder: d.bank_account_holder ?? '',
    iban: d.iban ?? '',
    bic: d.bic ?? '',
  };
}

export function buildCompanySavePayload(
  card: CompanySettingsCardId,
  form: CompanyFormState
): Record<string, unknown> {
  switch (card) {
    case 'unternehmen':
      return {
        company_name: form.company_name,
        street: form.street,
        house_number: form.house_number,
        postal_code: form.postal_code,
        city: form.city,
        country_code: 'DE',
        state_code: form.state_code,
      };
    case 'kontakt':
      return { phone: form.phone, email: form.email, website: form.website };
    case 'rechtlich':
      return {
        director_role: form.director_role,
        responsible_persons: form.responsible_persons,
        vat_id: form.vat_id,
        tax_number: form.tax_number,
        registry_court: form.registry_court,
        commercial_register_number: form.commercial_register_number,
      };
    case 'bank':
      return {
        bank_name: form.bank_name,
        bank_account_holder: form.bank_account_holder,
        iban: form.iban,
        bic: form.bic,
      };
    default:
      return {};
  }
}

function namedResponsiblePersons(persons: unknown): CompanyResponsiblePerson[] {
  if (!Array.isArray(persons)) return [];
  return persons
    .map((p) => {
      const name = p && typeof p === 'object' && 'name' in p && typeof p.name === 'string' ? p.name.trim() : '';
      return { name };
    })
    .filter((p) => p.name.length > 0);
}

/** True when the open card's fields differ from the revert snapshot. */
export function companyCardIsDirty(
  card: CompanySettingsCardId,
  form: CompanyFormState,
  revert: Partial<CompanyFormState> | null,
): boolean {
  if (!revert) return false;
  const current = buildCompanySavePayload(card, form);
  const previous = buildCompanySavePayload(card, { ...emptyCompanyForm, ...revert });
  if (card === 'rechtlich') {
    return (
      JSON.stringify({ ...current, responsible_persons: namedResponsiblePersons(current.responsible_persons) }) !==
      JSON.stringify({ ...previous, responsible_persons: namedResponsiblePersons(previous.responsible_persons) })
    );
  }
  return JSON.stringify(current) !== JSON.stringify(previous);
}
